<template>
  <validation-observer ref="goalSummary">
    <div class="achievement-container">
      <section-title has-line title="Resúmenes de Logros Sometidos" />
      <div v-if="editing" class="row mx-0 rounded bg-secondary-13 p-3">
        <div class="col-12 col-md-6 pl-md-0 mb-3 mb-md-0">
          <validation-provider v-slot="{ errors }" rules="required">
            <custom-drop-down
              v-model="model.period"
              class="dropdown"
              custom-error-msg="Favor seleccione el periodo académico."
              :error="errors[0]"
              fieldkey="periodId"
              fieldname="name"
              label="Periodo Académico:"
              :options="periods"
              placeholder="Seleccione"
            />
          </validation-provider>
        </div>
        <div class="col-12 col-md-6 pl-md-0 mb-3 mb-md-0">
          <validation-provider v-slot="{ errors }" rules="required">
            <custom-drop-down
              v-model="model.specialist"
              class="dropdown"
              custom-error-msg="Favor seleccione el especialista que discutió logros."
              :error="errors[0]"
              fieldkey="specialistId"
              fieldname="name"
              label="Especialista que discutió Logros:"
              :options="specialists"
              placeholder="Seleccione"
            />
          </validation-provider>
        </div>
        <template>
          <div class="col-12 my-3 px-md-0">
            <validation-provider v-slot="{ errors }" rules="required">
              <custom-text-area
                id="comment"
                v-model="model.achievement"
                class="achievement-container__text-area"
                :count-classes="['d-block', 'small-counter']"
                custom-error-msg="Favor describa brevemente los logros del estudiante."
                :error="errors[0]"
                :label="inputLabel(model.period)"
                :maxlength="3000"
                name="comment"
                show-char-count
              />
            </validation-provider>
          </div>
        </template>
        <div
          class="col-12 col-md-8 offset-md-4 text-right align-self-end px-md-3 mt-4"
        >
          <action-button
            i="fas fa-times-circle"
            text="Cancelar"
            variant="outline-danger mr-3 mb-2 my-md-0"
            @click="cancel"
          />
          <action-button
            i="fas fa-flag-alt"
            text="Someter"
            variant="success"
            @click="saveAchievement"
          />
        </div>
      </div>
      <template v-else>
        <div
          v-if="periods.length && achievements.length <= 0"
          class="row mx-0 justify-content-center bg-secondary-13 py-3 px-2 mb-3"
        >
          <button
            class="btn col-12 col-md-6 bg-light-white py-0 font-weight-normal"
            @click="switchEdit"
          >
            <em class="fas fa-file-plus pr-3 text-green" />
            <span class="button-text">
              Presione aquí para añadir resumen de logro
            </span>
          </button>
        </div>
        <div
          v-for="(achieve, idx) of achievements"
          :key="`achieve-${idx}`"
          class="rounded bg-secondary-13 row mx-0 justify-content-between align-items-center mb-3 p-3"
        >
          <div class="col-12 row">
            <!-- Primera fila: Periodo / Sesiones / Especialista -->
            <div class="col-12 row">
              <!-- Periodo Académico -->
              <div class="col-12 col-md-4">
                <custom-input-display
                  v-model="achieve.period.name"
                  label="Periodo Académico:"
                  label-class="input-label"
                />
              </div>

              <!-- Especialista -->
              <div
                class="col-12"
                :class="{
                  'col-md-4': showNumberInput(achieve.period),
                  'col-md-8': !showNumberInput(achieve.period),
                }"
              >
                <custom-input-display
                  v-model="achieve.specialist.name"
                  label="Especialista que discutió logros:"
                  label-class="input-label"
                />
              </div>
            </div>

            <!-- Segunda fila: Logro + Botón Eliminar -->
            <div class="col-12 row mt-3 align-items-start">
              <!-- Logro -->
              <div
                class="col-12"
                :class="{
                  'col-md-11': canShowDeleteArchivement,
                  'col-md-12': !canShowDeleteArchivement,
                }"
              >
                <div class="text-format">{{ achieve.achievement }}</div>
              </div>

              <!-- Botón Eliminar -->
              <div
                v-if="canShowDeleteArchivement"
                class="col-12 col-md-1 d-flex justify-content-md-end mt-2 mt-md-0"
              >
                <action-button
                  i="fas fa-times-circle"
                  text="Eliminar"
                  variant="danger"
                  @click="deleteArchivement(achieve)"
                />
              </div>
            </div>
          </div>
        </div>
        <div
          v-if="periods.length && achievements.length <= 0"
          class="d-flex justify-content-end"
        >
          <action-button
            v-if="canShowButton"
            i="fas fa-file-plus"
            text="Añadir"
            variant="success"
            @click="switchEdit"
          />
        </div>
      </template>
    </div>
  </validation-observer>
</template>

<script>
import SectionTitle from 'SectionTitle';
import CustomTextArea from 'CustomTextArea';
import ActionButton from 'ActionButton';
import CustomDropDown from 'custom-drop-down';
import CustomInputDisplay from 'CustomInputDisplay';
import alertMixin from 'utils/mixins/alert.mixin';

export default {
  name: 'AchievementsSummary',
  components: {
    SectionTitle,
    CustomTextArea,
    ActionButton,
    CustomDropDown,
    CustomInputDisplay,
  },
  mixins: [alertMixin],
  props: {
    data: {
      type: Array,
      default: () => [],
    },
    periods: {
      type: Array,
      default: () => [],
    },
    readOnly: {
      type: Boolean,
      default: () => false,
    },
    specialists: {
      type: Array,
      default: () => [],
    },
  },
  data: () => ({
    model: {
      achievement: null,
      sessions: null,
      period: null,
      specialist: null,
    },
    achievements: [],
    editing: false,
  }),
  computed: {
    showNumberInput() {
      return (period) => period?.periodId == 6;
    },
    canShowButton() {
      return true;
    },
    canShowDeleteArchivement() {
      return true;
    },
    inputLabel() {
      return (period) =>
        period?.periodId == 6
          ? 'Describa brevemente las sesiones ofrecidas en este periodo y los aspectos sobre logros más sobresalientes en este periodo. Señale factores que pudieran influir sobre el logro de las metas:'
          : 'Describa brevemente los logros del estudiante, indicando si el progreso logrado es suficiente para permititrle a éste alcanzar las metas trazadas para el año escolar:';
    },
    actionItems() {
      return (item) => {
        return [
          {
            name: 'edit',
            label: 'Editar',
            icon: 'fas fa-edit color-primary-1',
            border: 'bottom',
            click: () => this.editAchieve(item),
          },
          {
            name: 'delete',
            label: 'Eliminar',
            icon: 'fas fa-times-circle color-primary-3',
            click: () => this.deleteAchieve(item.id),
          },
        ];
      };
    },
  },
  watch: {
    data: {
      immediate: true,
      handler(current) {
        if (current) {
          this.achievements = [...current];
        }
      },
    },
  },
  methods: {
    switchEdit() {
      this.editing = !this.editing;
      this.$emit('editing-achievement', this.editing);
    },
    async deleteArchivement(achieve) {
      const cleanName = achieve.period.name;
      const parsedName = cleanName.replace(/\d/g, '');
      const response = await this.ShowCancelModal(
        'Confirmación',
        `¿Esta seguro que desea eliminar el resumen de logros de <b>${parsedName}</b> ?`,
        'Si',
        'No'
      );
      if (!response) {
        return;
      }
      this.$emit('delete-archivement', achieve.id);
    },
    async saveAchievement() {
      const isValid = await this.$refs.goalSummary.validate();
      if (!isValid) {
        this.$emit('alert');
        return;
      }

      if (this.model.id == null) {
        this.model.id = Math.random() * 1000;
      }
      this.$emit('save', this.model);
      this.cancel();
    },
    cancel() {
      this.model = {};
      this.switchEdit();
    },
    editAchieve(item) {
      this.model = { ...item };
      this.switchEdit();
    },
    deleteAchieve(id) {
      this.$emit('delete-achieve', id);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@/shared/scss/config/colors.scss';

.achievement-container {
  margin-top: 1.5rem;

  .text-format {
    overflow-wrap: break-word;
    text-align: justify;
  }

  .button-text {
    font-size: 0.9rem;
  }

  ::v-deep &__text-area {
    .small-counter {
      font-size: 0.75rem;
      color: map-get($colors, 'gray-6');
    }
  }

  .label {
    font-weight: normal;
    font-size: 0.875rem;
    color: map-get($colors, 'gray-6');
  }

  ::v-deep .input-display {
    margin: 0;
    .input-label {
      @extend .label;
    }
  }
}
</style>
